import React, { useState, useContext, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  Box,
  Popover,
  List,
  ListItemButton,
  Divider,
  Avatar,
  ListItemText,
  Button,
  Tooltip,
  LinearProgress,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  X,
  Bell,
  User,
  Lock,
  LogOut,
  LayoutGrid,
  Download,
  BellOff,
  Users,
  ChevronDown,
  ChevronUp,
  UserCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserProvider";
import useAdminEventSettings from "../../hooks/useAdminEventSettings";
import { API_ENDPOINT, USER_ROLES } from "../../utils/constants";
import axios from "axios";
import { toastError } from "../../utils/util";
import { getHeader, saveToken } from "../../utils/utils2";

export const AppBarHeader = ({ open, handleDrawerOpen }) => {
  const { data } = useAdminEventSettings();
  const { user, loading } = useContext(UserContext);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [anchorNotificationEl, setAnchorNotificationEl] = useState(null);

  // State for the Expandable Submenu
  const [isSwitchRoleOpen, setIsSwitchRoleOpen] = useState(false);

  const isMenuOpen = Boolean(anchorEl);
  const isNotificationOpen = Boolean(anchorNotificationEl);

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsSwitchRoleOpen(false); // Reset submenu state when main menu closes
  };

  const handleNotificationOpen = (event) =>
    setAnchorNotificationEl(event.currentTarget);
  const handleNotificationClose = () => setAnchorNotificationEl(null);

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  const notificationCount = 0;
  const sampleNotifications = [];

  const handleNotificationClick = (link) => {
    handleNotificationClose();
    navigate(link);
  };

  // Toggle for the expandable Switch Role menu
  const handleSwitchRoleToggle = (e) => {
    e.stopPropagation(); // Prevents menu from closing
    setIsSwitchRoleOpen(!isSwitchRoleOpen);
  };

  const handleSwitchRole = useCallback(async (targetRole) => {
    handleMenuClose();
    try {
      setSwitchingRole(true);
      const response = await axios.post(
        `${API_ENDPOINT}/user/switch-user`,
        {
          targetUserRole: targetRole,
        },
        getHeader()
      );
      saveToken(response.data.data.token);
      document.location.reload();
    } catch (error) {
      toastError(error);
    } finally {
      setSwitchingRole(false);
    }
  }, []);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <AppBar
      position="fixed"
      color="default"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: "rgba(255, 255, 255, 0.96)",
        color: "#172033",
        borderBottom: "1px solid #e5eaf2",
        boxShadow: "0 4px 18px rgba(20, 36, 66, 0.05)",
        backdropFilter: "blur(12px)",
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, px: { xs: 1.5, sm: 2.5, md: 3 } }}>
        {/* --- Improved Drawer Toggle --- */}
        <Tooltip title={open ? "Close menu" : "Open menu"}>
          <IconButton
            aria-label="toggle drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              mr: { xs: 1, sm: 1.75 },
              width: 42,
              height: 42,
              color: "#344054",
              bgcolor: open ? "#edf3ff" : "#f5f7fb",
              border: "1px solid #e5eaf2",
              borderRadius: 2.5,
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#2f66ee",
                bgcolor: "#edf3ff",
              },
            }}
          >
            {open ? <X size={24} /> : <MenuIcon size={24} />}
          </IconButton>
        </Tooltip>

        {/* --- Logo and Title Container --- */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, minWidth: 0 }}>
          {data?.eventLogo ? (
            <Box
              component="img"
              src={data.eventLogo}
              alt="Event Logo"
              sx={{
                height: { xs: 34, sm: 40 },
                maxWidth: { xs: 105, sm: 160 },
                width: "auto",
                objectFit: "contain",
              }}
            />
          ) : (
            <Box sx={{ width: 40, height: 40, borderRadius: 2.25, bgcolor: "#2f66ee", color: "white", display: "grid", placeItems: "center", flexShrink: 0 }}>
              <LayoutGrid size={21} />
            </Box>
          )}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ fontWeight: 800, fontSize: { xs: 17, sm: 20 }, maxWidth: { xs: 135, sm: 320 }, color: "#172033", letterSpacing: "-0.02em" }}
          >
            {data?.siteTitle ?? "ExpoPlanner"}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* --- Notification Icon --- */}
        <Tooltip title="Notifications">
          <IconButton
            onClick={handleNotificationOpen}
            sx={{
              mr: { xs: 0.5, sm: 1 },
              width: 40,
              height: 40,
              color: "#526174",
              bgcolor: "#f7f9fc",
              border: "1px solid #e8edf4",
              "&:hover": { color: "#2f66ee", bgcolor: "#edf3ff" },
            }}
          >
            <Badge badgeContent={notificationCount} color="error">
              <Bell size={22} />
            </Badge>
          </IconButton>
        </Tooltip>

        <Popover
          open={isNotificationOpen}
          anchorEl={anchorNotificationEl}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ mt: 1.5 }}
          PaperProps={{ sx: { width: "min(360px, calc(100vw - 24px))", borderRadius: 3, border: "1px solid #e5eaf2", boxShadow: "0 18px 48px rgba(20,36,66,.16)" } }}
        >
          {/* Notifications Content... (Kept identical) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              pb: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Notifications
            </Typography>
            {sampleNotifications.length > 0 && (
              <Button size="small" sx={{ textTransform: "none" }}>
                Mark all as read
              </Button>
            )}
          </Box>
          <Divider />

          <List sx={{ p: 0, maxHeight: 400, overflowY: "auto" }}>
            {sampleNotifications.length > 0 ? (
              sampleNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification.link)}
                    sx={{ p: 2, alignItems: "flex-start" }}
                  >
                    <ListItemIcon
                      sx={{ mt: 0.5, minWidth: 40, color: "primary.main" }}
                    >
                      {notification.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={notification.text}
                      secondary={notification.description}
                      primaryTypographyProps={{
                        variant: "subtitle2",
                        fontWeight: 600,
                      }}
                      secondaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItemButton>
                  {index < sampleNotifications.length - 1 && (
                    <Divider component="li" />
                  )}
                </React.Fragment>
              ))
            ) : (
              <Box
                sx={{
                  p: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  opacity: 0.5,
                }}
              >
                <BellOff
                  size={40}
                  strokeWidth={1.5}
                  style={{ marginBottom: 8 }}
                />
                <Typography variant="body2">You're all caught up!</Typography>
              </Box>
            )}
          </List>
        </Popover>

        {/* --- Profile Icon (Avatar) --- */}
        <Tooltip title="Account settings">
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              p: 0.5,
              pl: { xs: 0.5, md: 0.75 },
              pr: { xs: 0.5, md: 1.25 },
              ml: { xs: 0, sm: 0.5 },
              border: "1px solid #e5eaf2",
              borderRadius: 2.5,
              transition: "background-color 0.2s, border-color 0.2s",
              "&:hover": { borderColor: "#cbd8ef", bgcolor: "#f7f9fc" },
            }}
          >
            <Avatar
              src={user?.profilePic}
              alt={user?.name || "User"}
              sx={{ width: 36, height: 36, bgcolor: "#2f66ee", fontWeight: 700, fontSize: 14 }}
            >
              {getInitials(user?.name)}
            </Avatar>
            <Box sx={{ display: { xs: "none", md: "block" }, textAlign: "left", ml: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ maxWidth: 135, fontSize: 13.5, lineHeight: 1.25, fontWeight: 750, color: "#172033" }}>
                {user?.name || "Admin"}
              </Typography>
              <Typography noWrap sx={{ maxWidth: 135, fontSize: 11.5, lineHeight: 1.25, color: "#7b8799", textTransform: "capitalize" }}>
                {user?.role || "Administrator"}
              </Typography>
            </Box>
            <ChevronDown
              size={17}
              color="#7b8799"
              style={{
                marginLeft: 6,
                transition: "transform 180ms ease",
                transform: isMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </IconButton>
        </Tooltip>

        {/* --- Profile Dropdown Menu --- */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ mt: 1.5 }}
          PaperProps={{
            sx: { width: "min(300px, calc(100vw - 24px))", borderRadius: 3, border: "1px solid #e5eaf2", boxShadow: "0 18px 48px rgba(20,36,66,.16)", pb: 1 },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {user?.name || "User Name"}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user?.email || "user@example.com"}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: "inline-block",
                mt: 0.5,
                px: 1,
                py: 0.25,
                bgcolor: "primary.light",
                color: "white",
                borderRadius: 1,
              }}
            >
              {user?.role?.toUpperCase() || "ADMIN"}
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate("/profile");
            }}
          >
            <ListItemIcon>
              <User size={18} />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate("/change-password");
            }}
          >
            <ListItemIcon>
              <Lock size={18} />
            </ListItemIcon>
            <ListItemText primary="Change Password" />
          </MenuItem>

          {/* Expandable Switch Role Submenu */}
          {(user?.role === USER_ROLES.SUPER ||
            user?.role === USER_ROLES.OES) && (
            <>
              <MenuItem onClick={handleSwitchRoleToggle}>
                <ListItemIcon>
                  <Users size={18} />
                </ListItemIcon>
                <ListItemText primary="Switch Login" />
                {isSwitchRoleOpen ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </MenuItem>

              <Collapse in={isSwitchRoleOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {user?.role === USER_ROLES.SUPER && (
                    <MenuItem
                      disabled={switchingRole}
                      sx={{ pl: 4 }}
                      onClick={() => handleSwitchRole(USER_ROLES.OES)}
                    >
                      <ListItemIcon>
                        <UserCircle size={16} />
                      </ListItemIcon>
                      <ListItemText
                        primary="OES Account"
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </MenuItem>
                  )}

                  {(user?.role === USER_ROLES.SUPER ||
                    user?.role === USER_ROLES.OES) && (
                    <MenuItem
                      disabled={switchingRole}
                      sx={{ pl: 4 }}
                      onClick={() =>
                        handleSwitchRole(USER_ROLES.ADMIN || "ADMIN")
                      }
                    >
                      <ListItemIcon>
                        <UserCircle size={16} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Admin Account"
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </MenuItem>
                  )}
                </List>
              </Collapse>
            </>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Export Data conditionally rendered */}
          {user?.role !== USER_ROLES.TD_ADMIN && (
            <>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate("/export-data");
                }}
                sx={{ py: 1 }}
              >
                <ListItemIcon>
                  <Download size={20} />
                </ListItemIcon>
                <ListItemText
                  primary="Export Data"
                  secondary="Download reports"
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </MenuItem>

              <Divider sx={{ my: 1 }} />
            </>
          )}

          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate("/logout");
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <LogOut size={18} color="inherit" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
